import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, DirectionsRenderer, useLoadScript, OverlayView } from "@react-google-maps/api";
import { setDefaults, fromLatLng } from "react-geocode";
import "./styles.css";
import i18n from "../../../i18n";
import config from '../../../constants/settings';
import { mapOptions } from "./mapOptions";
import { TypeAddress } from "../../../constants/enums";
import scalesMarker from "../../../assets/images/markers/scales.png";
import destinyMarker from "../../../assets/images/markers/destiny.png";
import originMarker from "../../../assets/images/markers/origin.png";

export default function MapServices({
  selectedOrigin,
  selectedDestiny,
  setSelectedOrigin,
  setSelectedDestiny,
  setSelectedAddressDestiny,
  selectedAddress,
  titleAdressDestiny,
  setAddressesMarkers,
  addressesMarkers,
  setShowUnits,
  setShowAddresses,
  showAddresses,
  setNameTitleOrigin,
  setSelectedAddressOrigin,
  setNameTitleDestiny,
  titleAdressOrigin,
  setNewCreateAddressEmployee,
  rowsIndex,
  servicesView,
  typeAddress,
  typeService,
  setShowEmployees,
  setArrScales,
  arrScales,
  setFindEmployees,
  findEmployees,
  setOnFocusAddress,
  setSelectedAddress,
  titleAdressArrScales,
  titleAdressArrEmployees,
  setNameTitleArrScales,
  setNameTitleEmployees,
  selectedAddressOrigin,
  selectedAddressDestiny
}) {

  const [libraries] = useState(['places']);
  const [oldAddress, setOldAddress] = useState({});
  const [selected, setSelected] = useState({});
  const [directions, setDirections] = useState(null);
  const [mapZoom, setMapZoom] = useState(10);
  const [isInitialCenter, setIsInitialCenter] = useState(true);
  const [center, setCenter] = useState({ lat: 19.432608, lng: -99.133209 });
  const mapRef = useRef(null);


  const containerStyle = {
    height: '500px',
    width: '100%'
  };

  const defaultCenter = { lat: 19.432608, lng: -99.133209 };

  useEffect(() => {
    if (isInitialCenter && mapRef.current) {
      mapRef.current.panTo(defaultCenter);
      setIsInitialCenter(false);
    }
  }, [isInitialCenter]);

  useEffect(() => {
    if (selectedOrigin) {
      setCenter(selectedOrigin);
      setMapZoom(16);
      if (mapRef.current) {
        mapRef.current.panTo(selectedOrigin.location);
      }
    }
  }, [selectedOrigin]);

  useEffect(() => {
    if (selectedDestiny) {
      setCenter(selectedDestiny);
      setMapZoom(14);
      if (mapRef.current) {
        mapRef.current.panTo(selectedDestiny.location);
      }
    }
  }, [selectedDestiny]);

  useEffect(() => {
    let findAddress = addressesMarkers.filter((item) => {
      return item.typeAddress === TypeAddress.ARRAY_EMPLOYEES;
    }).shift();
    
    if (findAddress) {
      setSelected({ lng: findAddress.location.lng, lat: findAddress.location.lat });
    }
  }, [addressesMarkers]);

  useEffect(() => {
    if (selectedOrigin && selectedDestiny) {
      calculateRoute(selectedOrigin, selectedDestiny, arrScales);
    }
  }, [selectedOrigin, selectedDestiny, arrScales]);
  
  useEffect(() => {
    if (selectedOrigin && selectedDestiny && arrScales.length) {
      calculateRoute(selectedOrigin, selectedDestiny, arrScales);
    }
  }, [selectedOrigin, selectedDestiny, arrScales]);

  setDefaults({
    key: config.google.secret,
    language: "es",
    region: "mx",
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.google.secret,
    libraries: libraries,
    language: "es",
    region: "mx",
  });

  const onClickAccept = () => {
    setShowEmployees(true);
    setShowAddresses(false);

    if (servicesView && typeAddress === TypeAddress.ARRAY_EMPLOYEES) {
      let onAddressesMarkers = addressesMarkers;
      let findAddress = addressesMarkers.filter((item) => {
        return item.typeAddress === TypeAddress.ARRAY_EMPLOYEES;
      }).shift();
      
      if (findAddress) {
        onAddressesMarkers.splice(onAddressesMarkers.indexOf(findAddress), 1);
        setAddressesMarkers([...onAddressesMarkers]);
        setNewCreateAddressEmployee({ ...findAddress });
      }
    }
  };

  const onClickCancel = () => {
    return true;
  };

  const calculateRoute = async (origin, destination, waypoints = []) => {
    if (!origin || !destination) {
      setDirections(null);
      return;
    }
  
    const directionsService = new window.google.maps.DirectionsService();
    
    const originLocation = origin.location 
      ? { lat: origin.location.lat, lng: origin.location.lng || origin.location.lon }
      : origin;
    
    const destinationLocation = destination.location 
      ? { lat: destination.location.lat, lng: destination.location.lng || destination.location.lon }
      : destination;
  
    try {
      const formattedWaypoints = waypoints
        .filter(scale => scale.location)
        .map((scale) => ({
          location: {
            lat: scale.location.lat,
            lng: scale.location.lng || scale.location.lon
          },
          stopover: true
        }));
  
      const request = {
        origin: originLocation,
        destination: destinationLocation,
        waypoints: formattedWaypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
        provideRouteAlternatives: false
      };
  
      const result = await directionsService.route(request);
      
      if (result.status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error("Error calculating route:", result.status);
        setDirections(null);
      }
    } catch (error) {
      console.error("Error calculating route:", error);
      setDirections(null);
    }
  };

  const getNameAddress = () => {
    let name = "";
    switch (typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        name = i18n.t("Scale");
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        name = i18n.t("Employee");
        break;
      case TypeAddress.DESTINY:
        name = i18n.t("Destiny");
        break;
      case TypeAddress.ORIGIN:
        name = i18n.t("Origin");
        break;
    }
    return name;
  };

  const getMarkerIcon = (item) => {
    let name = "";
    switch (item.typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        name = scalesMarker;
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        name = `marker-employee.png`;
        break;
      case TypeAddress.DESTINY:
        name = destinyMarker;
        break;
      case TypeAddress.ORIGIN:
        name = originMarker;
        break;
    }
    return name;
  };

  const getLabelMarker = (item) => {
    let name = "";
    let address = item?.description ?? ""; 
    switch (item.typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        name = item.employeeId 
          ? i18n.t("EmployeeScale")
          : i18n.t("ScaleNumber", { number: item.id - 2 });
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        name = i18n.t("Employee");
        break;
      case TypeAddress.DESTINY:
        name = i18n.t("Destiny");
        break;
      case TypeAddress.ORIGIN:
        name = i18n.t("Origin");
        break;
    }
    return (
      <div className="custom-label">
        <div className="marker-label-name">{name}</div>
        <div className="marker-label-address">{address}</div>
      </div>
    );
  };

  const CustomMarker = ({ item, onMarkerDragEnd }) => {
    const markerPosition = item.location;
    const [isHovered, setIsHovered] = useState(false);
  
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
  
    const alwaysShowLabel = 
      item.typeAddress === TypeAddress.ORIGIN || 
      item.typeAddress === TypeAddress.DESTINY;
  
    return (
      <>
        <Marker
          key={item.id}
          icon={getMarkerIcon(item)}
          draggable={item.dragg}
          onDragEnd={(t, map, coord) => onMarkerDragEnd(t, item)}
          position={markerPosition}
          onMouseOver={handleMouseEnter}
          onMouseOut={handleMouseLeave}
        />
        {(alwaysShowLabel || isHovered) && (
          <OverlayView
            position={markerPosition}
            mapPaneName={OverlayView.FLOAT_PANE}
          >
            <div>{getLabelMarker(item)}</div>
          </OverlayView>
        )}
      </>
    );
  };  

  const onMarkerDragEnd = (coord, address) => {
    const { latLng } = coord;
    const lat = latLng.lat();
    const lng = latLng.lng();
  
    fromLatLng(lat, lng)
      .then(({ results }) => {
        const newLocation = { lat, lng, lon: lng, long: lng };
        const formattedAddress = results[0].formatted_address;
  
        const updatedAddress = {
          ...address,
          location: newLocation,
          description: formattedAddress,
        };
  
        setAddressesMarkers((prevMarkers) =>
          prevMarkers.map((marker) =>
            marker.id === address.id ? updatedAddress : marker
          )
        );
  
        switch (address.typeAddress) {
          case TypeAddress.ORIGIN:
            setSelectedOrigin(updatedAddress);
            setSelectedAddressOrigin(updatedAddress);
            setNameTitleOrigin(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          case TypeAddress.DESTINY:
            setSelectedDestiny(updatedAddress);
            setSelectedAddressDestiny(updatedAddress);
            setNameTitleDestiny(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          case TypeAddress.ARRAY_SCALES:
            setArrScales((prevScales) =>
              prevScales.map((scale) =>
                scale.id === address.id ? updatedAddress : scale
              )
            );
            setNameTitleArrScales(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          default:
            break;
        }
        
        setOnFocusAddress(address.typeAddress);
        calculateRoute(
          address.typeAddress === TypeAddress.ORIGIN ? updatedAddress : selectedOrigin,
          address.typeAddress === TypeAddress.DESTINY ? updatedAddress : selectedDestiny,
          address.typeAddress === TypeAddress.ARRAY_SCALES
            ? arrScales.map((scale) =>
                scale.id === address.id ? updatedAddress : scale
              )
            : arrScales
        );
      })
      .catch((error) => {
        console.error("Error - fromLatLng - ", error);
      });
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <>
      <div className={`places-container ${typeAddress === "array" ? 'col-12' : ''}`}>
        <h6>{typeAddress === TypeAddress.DESTINY && titleAdressDestiny}</h6>
        <h6>{typeAddress === TypeAddress.ORIGIN && titleAdressOrigin}</h6>
        <h6>{typeAddress === TypeAddress.ARRAY_SCALES && titleAdressArrScales}</h6>
        <h6>{typeAddress === TypeAddress.ARRAY_EMPLOYEES && titleAdressArrEmployees}</h6>
        {(selectedAddressOrigin.description || selectedAddressDestiny.description) && <span>{i18n.t("SubtitleAddress")}</span>}
        <GoogleMap
          zoom={mapZoom}
          center={center}
          mapContainerStyle={containerStyle}
          mapContainerClassName="map-container"
          options={mapOptions}
        >
          {addressesMarkers.length !== 0 &&
            addressesMarkers
              .filter(marker => marker.location)
              .map((item) => (
                <CustomMarker
                  key={`${item.typeAddress}-${item.id}`}
                  item={{
                    ...item,
                    location: {
                      lat: item.location.lat,
                      lng: item.location.lng || item.location.lon
                    }
                  }}
                  onMarkerDragEnd={onMarkerDragEnd}
                />
              ))}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                polylineOptions: {
                  strokeColor: "#FFCBAD",
                  strokeWeight: 5,
                  strokeOpacity: 1,
                },
                suppressMarkers: true,
              }}
            />
          )}
        </GoogleMap>
        {/* <div className="row pt-3 text-center">
          <div className="col-2">
            { typeAddress != "arrayEmployees" ? <button className="btn btn-danger" disabled={(!showAddresses)} onClick={() => onClickAccept()}>{i18n.t("Accept")}</button> :
            <button className="btn btn-danger" disabled={(rowsIndex)} onClick={() => onClickAccept()}>{i18n.t("Accept")}</button>}
          </div>
          <div className="col-2 text-start">
            <button className="btn border" disabled={!showAddresses} onClick={() => onClickCancel()}>{i18n.t("Cancel")}</button>
          </div>
        </div> */}
      </div>
    </>
  );
}
